import { ShowerHead, Toilet, DoorOpen } from "lucide-react";
import ImageReveal from "./ImageReveal";
import Reveal from "./Reveal";
import { QuoteButton } from "./Buttons";

const DEFAULT_PAIRINGS = [
  { icon: ShowerHead, label: "Shower + toilet pairings" },
  { icon: DoorOpen, label: "Entry + hallway support" },
  { icon: Toilet, label: "Whole-bathroom setups" },
];

export default function MultiRailPromo({
  variant = "full",
  kicker = "Multi-rail offer",
  title = "ONE RAIL HELPS. THE RIGHT FEW CHANGE EVERYTHING.",
  body1 = "A grab rail only works when it's within reach — and the moments that matter happen all around the room: stepping out of the shower, turning at the vanity, lowering onto the toilet. Support at each tricky spot means confidence carries through the whole room, not just one corner.",
  body2Strong = "3 or more grab rails",
  body2Rest = " Send us a photo of each area and we'll put together a package quote — one visit, one professional installation, better value. Just mention multi-rail package pricing when you enquire.",
  chips = DEFAULT_PAIRINGS,
  image = "/images/grab-04.jpg",
  imageAlt = "Shower grab rail installed in a Perth home to reduce slip and fall risk",
  service = "Grab Rails",
  ctaLabel = "GET A MULTI-RAIL QUOTE",
}) {
  const body2Text = `Planning ${body2Strong}?${body2Rest}`;
  if (variant === "compact") {
    return (
      <section data-testid="multi-rail-promo" className="border-y border-line bg-sage-light">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
          <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
            <Reveal className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">{kicker}</p>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-charcoal sm:text-3xl">
                {title}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-ink/75">{body2Text}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <QuoteButton label={ctaLabel} service={service} offer testid="multi-rail-quote-button" />
            </Reveal>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="multi-rail-promo" className="bg-charcoal text-ivory">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <img src="/images/logo-white.png" alt="" aria-hidden="true" className="mb-7 w-28 opacity-90" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">{kicker}</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              {title}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg">{body1}</p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg">
              Planning <strong className="text-sage-tint">{body2Strong}</strong>?{body2Rest}
            </p>
            <ul className="mt-7 flex flex-wrap gap-2">
              {chips.map((p) => (
                <li
                  key={p.label}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-ivory/85"
                >
                  <p.icon className="h-4 w-4 text-sage-tint" aria-hidden="true" />
                  {p.label}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <QuoteButton label={ctaLabel} service={service} offer variant="light" testid="multi-rail-quote-button" />
            </div>
          </Reveal>
          <ImageReveal delay={0.1} radius="2rem" className="spotlight-frame overflow-hidden rounded-[2rem] lg:col-span-5">
            <img src={image} alt={imageAlt} loading="lazy" className="aspect-[4/5] w-full object-cover" />
          </ImageReveal>
        </div>
      </div>
    </section>
  );
}
